import { ApolloQueryResult } from '@apollo/client';

import { HookResult } from '@/store/apolloClient';
import { RealmRole, UsersPage, UserWithRole } from '@/administration/types/users';
import { AccountStatus, User } from '@/types/group';
import { useDebouncedQuery } from '@/hooks/useDebouncedQuery';
import { RUserStatusV2, UsersDocument } from '@/store/v2';

const DEBOUNCE_TIME = 800;

interface UsersFilter {
  searchPhrase?: string;
  idNIN?: number[];
  idIN?: number[];
  status?: AccountStatus[];
  statusV2IN?: RUserStatusV2[];
  statusV2NIN?: RUserStatusV2[];
  realm?: string;
  realmRole?: RealmRole;
}

interface UsersPagination {
  limit: number;
  page: number;
}

export enum UsersSortField {
  ID = 'ID',
  EMAIL = 'EMAIL',
  USERNAME = 'USERNAME',
  STATUS = 'STATUS',
}

export enum UsersSortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

export interface UsersSort {
  field: UsersSortField;
  order: UsersSortOrder;
}

export interface UsersOptions {
  withRealmRoleAssignments?: boolean;
  skip?: boolean;
}

interface UsersResponse extends HookResult {
  users: readonly UserWithRole[];
  total: number;
  refetch: () => Promise<ApolloQueryResult<{ users: UsersPage }>>;
}

export const useUsers = (
  filter: UsersFilter,
  pagination: UsersPagination,
  sort: UsersSort,
  options?: UsersOptions,
): UsersResponse => {
  const variables = {
    filter,
    sort,
    pagination,
    withRealmRoleAssignments: !!options?.withRealmRoleAssignments,
  };
  const { data, error, refetch, loading, networkStatus } = useDebouncedQuery<{ users: UsersPage }, typeof variables>(
    UsersDocument,
    {
      variables,
      skip: !!options?.skip,
      debounceDelay: DEBOUNCE_TIME,
      shouldDebounce: (prevVars, nextVars) => prevVars?.filter?.searchPhrase !== nextVars?.filter?.searchPhrase,
    },
  );
  const users: ReadonlyArray<User> = data?.users?.data || [];
  const total = data?.users?.total || 0;

  return {
    users,
    total,
    loading,
    error,
    refetch: () => refetch(variables),
    networkStatus,
  };
};
